import { useContext, useEffect, useState } from "react";
import { RightDrawerContext } from "../context/RightDrawerContext";
import {
  AddMembersContext,
  ApiErrorHandler,
  ChatInfoContext,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import {
  DrawerDivider,
  DrawerHeader,
  EmptyBox,
} from "../layout/RightDrawer.style";
import AddMember from "../addMember/AddMember";
import { AddInternalGroupInfo } from "./AddInternalGroupInfo";
import {
  IFile,
  IGroupInfo,
  IInternalGroupData,
  IMediaDraft,
  UploadProgressData,
  UploadSuccessData,
} from "@home-health-notify/domain";
import CommonDrawerHeader from "../common/CommonDrawerHeader";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { AddGroupWrapper } from "../common/Common.style";
import {
  ICreateInternalGroupRequest,
  IEditInternalGroupRequest,
} from "@home-health-notify/models";
import { uploadToCloudStorage } from "../utils/WebCloudStoargeUploader";

type EditGroupProps = {
  chatId?: string;
};

export const AddInternalGroup = ({ chatId }: EditGroupProps) => {
  const { t } = useTranslation();
  const tNsDrawer = (key: string) => t(key, { ns: "drawerHeader" });
  const tNs = (key: string) => t(key, { ns: "internalGroup" });

  const { closeDrawer, openChatInfoDrawer } = useContext(RightDrawerContext);
  const { enqueueSnackbar } = useSnackbar();
  const { resetMembers } = useContext(AddMembersContext);

  const [showAddMembers, setShowAddMembers] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { selectedOrgUser } = useContext(OrganizationContext);

  const isEdit = !!chatId;

  const { chat } = useContext(ChatInfoContext);
  const groupInfo = isEdit ? chat && (chat?.chatInfo as IGroupInfo) : undefined;

  const [internalGroupData, setInternalGroupData] =
    useState<IInternalGroupData>(
      groupInfo
        ? {
            image: groupInfo.image || "",
            groupName: groupInfo.name,
            about: groupInfo.about,
          }
        : ({} as IInternalGroupData),
    );
  const [groupImage, setGroupImage] = useState<IMediaDraft>();

  useEffect(() => {
    setInternalGroupData({
      image: groupInfo?.image || "",
      groupName: groupInfo?.name || "",
      about: groupInfo?.about || "",
    });
  }, [groupInfo]);

  const onSetInternalGroupData = (data: IInternalGroupData) => {
    setInternalGroupData(data);
  };

  const addInternalGroupInfo = (internalGroupData: IInternalGroupData) => {
    setInternalGroupData(internalGroupData);
    if (isEdit) {
      uploadImageAndAddInternalGroup(undefined, internalGroupData);
    } else {
      setShowAddMembers(true);
    }
  };

  const resetForm = () => {
    setInternalGroupData({} as IInternalGroupData);
    setShowAddMembers(false);
  };

  const onUploadProgress = (uploadProgressData: UploadProgressData) => {};
  const onUploadSuccess = (selectedMembers?: any) => {
    return async (uploadSuccessData: UploadSuccessData) => {
      const fileUrl = uploadSuccessData.url;
      const internalGroupDataWithImage = {
        ...internalGroupData,
        image: fileUrl,
      };
      await addInternalGroup(selectedMembers, internalGroupDataWithImage);
    };
  };
  const onUploadError = (_file: IFile, _error: any) => {
    enqueueSnackbar(tNs("uploadError"));
  };

  const uploadImage = async (selectedMembers?: any) => {
    if (groupImage?.mediaFile) {
      await selectedOrgUser?.assetManager.uploadToSignedUrl(
        groupImage?.mediaFile,
        groupImage?.mediaFile.name,
        uploadToCloudStorage,
        onUploadProgress,
        onUploadSuccess(selectedMembers),
        onUploadError,
      );
    }
  };

  const uploadImageAndAddInternalGroup = async (
    selectedMembers?: any,
    internalGroupData?: IInternalGroupData,
  ) => {
    try {
      setIsLoading(true);
      if (groupImage) {
        await uploadImage(selectedMembers);
      } else {
        await addInternalGroup(selectedMembers, internalGroupData);
      }
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const addInternalGroup = async (
    selectedMembers?: any,
    internalGroupData?: IInternalGroupData,
  ) => {
    try {
      let internalGroupPayload;
      if (isEdit) {
        // Without an id the group endpoint upserts into a new group rather
        // than updating this one, so refuse to submit instead.
        const editId = groupInfo?.id;
        if (!editId) return;
        internalGroupPayload = {
          ...getEditRequestData(internalGroupData as IInternalGroupData),
          id: editId,
        } as IEditInternalGroupRequest;
      } else {
        internalGroupPayload = getRequestData(
          selectedMembers,
          internalGroupData as IInternalGroupData,
        );
      }

      await selectedOrgUser?.chatManager
        .getNonPatientChatManager()
        .addChat(internalGroupPayload);

      if (isEdit) {
        enqueueSnackbar(
          internalGroupPayload["name"] +
            " " +
            tNsDrawer("editGroupSuccessMessage"),
          {
            autoHideDuration: 3000,
          },
        );
      } else {
        enqueueSnackbar(
          internalGroupPayload["name"] +
            " " +
            tNsDrawer("createGroupSuccessMessage"),
          {
            autoHideDuration: 3000,
          },
        );
      }
      if (!isEdit) {
        closeDrawer();
      } else {
        openChatInfo();
      }
      resetForm();
      resetMembers();
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    }
  };

  const getEditRequestData = (
    internalGroupData: IInternalGroupData,
  ): IEditInternalGroupRequest => {
    let internalGroupPayload: Partial<ICreateInternalGroupRequest> = {};
    internalGroupPayload["about"] = internalGroupData.about;
    internalGroupPayload["name"] = internalGroupData.groupName;
    if (internalGroupData.image) {
      internalGroupPayload["image"] = internalGroupData.image;
    }
    return internalGroupPayload as IEditInternalGroupRequest;
  };

  const getRequestData = (
    selectedMembers: any,
    updatedInternalGroupData: IInternalGroupData,
  ): ICreateInternalGroupRequest => {
    const data = updatedInternalGroupData || internalGroupData;
    let internalGroupPayload: Partial<ICreateInternalGroupRequest> = {};
    if (selectedMembers.membersToAdd && selectedMembers.membersToAdd.length > 0)
      internalGroupPayload["membersToAdd"] = selectedMembers.membersToAdd;
    if (selectedMembers.groupsToAdd && selectedMembers.groupsToAdd.length > 0)
      internalGroupPayload["groupsToAdd"] = selectedMembers.groupsToAdd;
    internalGroupPayload["about"] = data.about;
    internalGroupPayload["name"] = data.groupName;
    if (data.image) {
      internalGroupPayload["image"] = data.image;
    }
    return internalGroupPayload as ICreateInternalGroupRequest;
  };
  const openChatInfo = () => {
    openChatInfoDrawer();
    resetForm();
  };

  return (
    <AddGroupWrapper>
      {isEdit ? (
        <DrawerHeader>
          <IconButton data-testid="back-button" onClick={() => openChatInfo()}>
            <ChevronLeftIcon fontSize={"large"} />
          </IconButton>
          <Typography variant="h6">{tNs("editInternalGroup")}</Typography>
          <EmptyBox />
        </DrawerHeader>
      ) : (
        <CommonDrawerHeader
          showBackButton={showAddMembers}
          onBackButtonClick={() => setShowAddMembers(false)}
          title={
            showAddMembers
              ? tNsDrawer("addMembers")
              : tNsDrawer("newInternalGroup")
          }
          onCloseButtonClick={() => {
            closeDrawer();
            setShowAddMembers(false);
            setInternalGroupData({} as IInternalGroupData);
            resetMembers();
          }}
        />
      )}
      <Divider />
      {showAddMembers ? (
        <AddMember
          callback={uploadImageAndAddInternalGroup}
          isLoading={isLoading}
        />
      ) : (
        <Box
          sx={{
            flexDirection: "column",
            overflow: "auto"
          }}>
          <DrawerDivider />
          <AddInternalGroupInfo
            addInternalGroupInfo={addInternalGroupInfo}
            onSetInternalGroupData={onSetInternalGroupData}
            internalGroupData={internalGroupData}
            groupInfo={groupInfo}
            setGroupImage={setGroupImage}
            isLoading={isLoading}
          />
        </Box>
      )}
    </AddGroupWrapper>
  );
};
